import { useState } from "react";
import { cn, RichTextDisplay } from "@tellescope/ui";
import {
  Info,
  CheckCircle,
  AlertTriangle,
  XCircle,
  X,
  Bell,
  Star,
  Heart,
  Zap,
  Shield,
  MessageCircle,
} from "lucide-react";
import type { BannerBlock as BannerBlockType, BannerCustomIcon } from "../../../shared/schema";
import { AsyncLinkInterceptor } from "../AsyncLinkInterceptor";

interface BannerBlockProps {
  content: BannerBlockType["props"]["content"];
  variant: BannerBlockType["props"]["variant"];
  dismissible: BannerBlockType["props"]["dismissible"];
  customBackgroundColor?: string;
  customTextColor?: string;
  customIcon?: BannerCustomIcon;
}

type BannerVariant = BannerBlockType["props"]["variant"];

const variantStyles: Record<Exclude<BannerVariant, "custom">, string> = {
  info: "bg-blue-50 border-blue-200 text-blue-800",
  success: "bg-green-50 border-green-200 text-green-800",
  warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
  error: "bg-red-50 border-red-200 text-red-800",
  neutral: "bg-gray-50 border-gray-200 text-gray-800",
};

const variantIcons: Record<Exclude<BannerVariant, "custom">, React.ElementType> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle,
  neutral: Info,
};

const iconStyles: Record<Exclude<BannerVariant, "custom">, string> = {
  info: "text-blue-500",
  success: "text-green-500",
  warning: "text-yellow-500",
  error: "text-red-500",
  neutral: "text-gray-500",
};

const CUSTOM_ICON_MAP: Record<BannerCustomIcon, React.ElementType> = {
  info: Info,
  "check-circle": CheckCircle,
  "alert-triangle": AlertTriangle,
  "x-circle": XCircle,
  bell: Bell,
  star: Star,
  heart: Heart,
  zap: Zap,
  shield: Shield,
  "message-circle": MessageCircle,
};

export const BannerBlock: React.FC<BannerBlockProps> = ({
  content,
  variant = "info",
  dismissible = false,
  customBackgroundColor,
  customTextColor,
  customIcon,
}) => {
  const [isDismissed, setIsDismissed] = useState(false);

  // Don't render empty banners or dismissed banners
  if (!content || isDismissed) return null;

  if (variant === "custom") {
    const Icon = customIcon ? CUSTOM_ICON_MAP[customIcon] : Info;
    const containerStyle: React.CSSProperties = {};
    if (customBackgroundColor) containerStyle.backgroundColor = customBackgroundColor;
    if (customTextColor) containerStyle.color = customTextColor;
    const iconStyle: React.CSSProperties = customTextColor ? { color: customTextColor } : {};

    return (
      <div
        className="relative flex items-start gap-3 rounded-lg border p-4 bg-gray-50 border-gray-200"
        style={containerStyle}
        role="alert"
      >
        <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" style={iconStyle} />
        <AsyncLinkInterceptor className="flex-1 min-w-0">
          <RichTextDisplay key={content} content={content} className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0" />
        </AsyncLinkInterceptor>
        {dismissible && (
          <button
            type="button"
            onClick={() => setIsDismissed(true)}
            className="flex-shrink-0 rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  const Icon = variantIcons[variant];

  return (
    <div className={cn("relative flex items-start gap-3 rounded-lg border p-4", variantStyles[variant])} role="alert">
      <Icon className={cn("h-5 w-5 flex-shrink-0 mt-0.5", iconStyles[variant])} />
      <AsyncLinkInterceptor className="flex-1 min-w-0">
        <RichTextDisplay key={content} content={content} className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0" />
      </AsyncLinkInterceptor>
      {dismissible && (
        <button
          type="button"
          onClick={() => setIsDismissed(true)}
          className={cn(
            "flex-shrink-0 rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2",
            variant === "info" && "focus:ring-blue-500",
            variant === "success" && "focus:ring-green-500",
            variant === "warning" && "focus:ring-yellow-500",
            variant === "error" && "focus:ring-red-500",
            variant === "neutral" && "focus:ring-gray-500"
          )}
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
